import { z } from 'zod';

export const DEFAULT_PAGE_LIMIT = 30;

export type NextCursor = Record<string, string | number | Date | boolean>;

export const encodeCursor = (cursor?: NextCursor) =>
  cursor ? Buffer.from(JSON.stringify(cursor)).toString('base64') : undefined;

export const createPageDTOSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    nextCursor: z.string().optional(),
  });

export const base64EncodedJsonSchema = z.base64().transform((value, ctx) => {
  try {
    const decoded = Buffer.from(value, 'base64').toString('utf8');
    return JSON.parse(decoded);
  } catch (_e) {
    ctx.addIssue({
      code: 'custom',
      message: 'decoded string is not a valid JSON',
    });
    return z.NEVER;
  }
});

export const createPageQueryDTOSchema = <T extends readonly ['id', ...string[]]>({
  sortableFields,
  defaultSortBy = 'id',
  defaultSortOrder = 'asc',
}: {
  sortableFields: T;
  defaultSortBy?: T[number];
  defaultSortOrder?: 'asc' | 'desc';
}) =>
  z
    .object({
      limit: z.coerce.number().int().positive().max(100).optional().default(DEFAULT_PAGE_LIMIT),
      sortBy: z.enum(sortableFields).optional().default(defaultSortBy),
      sortOrder: z.enum(['asc', 'desc']).optional().default(defaultSortOrder),
      cursor: base64EncodedJsonSchema.optional(),
    })
    .check((ctx) => {
      if (!ctx.value.cursor) {
        return;
      }

      const cursorKeys = Object.keys(ctx.value.cursor);

      // the cursor keys must be at most 2, id and if 2 the other one must be in sortBy
      if (cursorKeys.length > 2) {
        ctx.issues.push({
          code: 'custom',
          message: 'Cursor JSON must have at most 2 keys',
          input: ctx.value.cursor,
        });
      }

      if (
        cursorKeys.length === 2 &&
        (!cursorKeys.includes('id') || (ctx.value.sortBy && !cursorKeys.includes(ctx.value.sortBy)))
      ) {
        ctx.issues.push({
          code: 'custom',
          message: 'Cursor JSON must have id and match sortBy',
          input: ctx.value.cursor,
        });
      }
    });
